import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Modal } from '@mui/material';
import styles from './ButtonsStyles.module.scss';
import { botAiProvider } from '../ChatBotAI';
import { ACCEPTED_FILE_TYPES, dedupe, formatMB, isAudioFile, isFileImage, isPDF, isTextFile, isVideoFile, MAX_FILES, MAX_TOTAL_BYTES, MAX_TOTAL_MB, truncate } from '../ChatBotAIUtils';

export const FileButton = () => {
  const [files, setFiles] = useState([] as File[]);
  const [fileUrls, setFileUrls] = useState<Record<string, string>>({});
  const [openImagePreview, setOpenImagePreview] = useState<File | null>(null);
  const [zoom, setZoom] = useState(1);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    (botAiProvider as any).setGetPendingFiles?.(() => files);
    (botAiProvider as any).setClearPendingFiles?.(() => {
      Object.values(fileUrls)?.forEach((url) => URL.revokeObjectURL(url));
      setFileUrls({});
      setFiles([]);
    });

    return () => {
      Object.values(fileUrls)?.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [files, fileUrls]);

  const openPicker = () => inputRef.current?.click();

  const getUrlForFile = (f: File) => {
    const key = `${f.name}_${f.size}_${f.lastModified}`;

    return fileUrls[key] || URL.createObjectURL(f);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fl = e.target.files;
    if (!fl) return;

    const picked = Array.from(fl);
    const newUrls = { ...fileUrls };

    picked?.forEach((f) => {
      const key = `${f.name}_${f.size}_${f.lastModified}`;

      if (!newUrls[key]) {
        newUrls[key] = URL.createObjectURL(f);
      }
    });

    setFiles((prev) => {
      const next = dedupe([...prev]);
      let totalBytes = next.reduce((acc, f) => acc + f.size, 0);

      for (const f of picked) {
        if (next.length >= MAX_FILES) break;
        const exists = next.some((x) => x.name === f.name && x.size === f.size && x.lastModified === f.lastModified);
        if (exists) continue;
        if (totalBytes + f.size > MAX_TOTAL_BYTES) continue;

        next.push(f);
        totalBytes += f.size;
      }

      return dedupe(next).slice(0, MAX_FILES);
    });

    setFileUrls(newUrls);
    e.target.value = '';
  };

  const onClickButtonAttach = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (files.length >= MAX_FILES) return;
    openPicker();
  };

  const remove = (idx: number) => {
    setFiles((prev) => {
      const copy = [...prev];
      const [removed] = copy.splice(idx, 1);

      if (removed) {
        const key = `${removed.name}_${removed.size}_${removed.lastModified}`;
        const url = fileUrls[key];

        if (url) {
          URL.revokeObjectURL(url);
        }

        setFileUrls((currentUrls) => {
          const newUrls = { ...currentUrls };
          delete newUrls[key];

          return newUrls;
        });
      }

      return copy;
    });
  };

  const { totalBytes, percentage, color } = useMemo(() => {
    const total = files.reduce((acc, f) => acc + f.size, 0);
    const percentageRaw = total ? (total / MAX_TOTAL_BYTES) * 100 : 0;
    const col = percentageRaw <= 33 ? '#3fb950' : percentageRaw <= 66 ? '#f2a516' : '#e5534b';

    return { totalBytes: total, percentage: Math.min(100, percentageRaw), color: col };
  }, [files]);

  const attachDisabled = files.length >= MAX_FILES || totalBytes >= MAX_TOTAL_BYTES;

  const handleWheel: React.WheelEventHandler<HTMLDivElement> = (ev) => {
    const delta = ev.deltaY > 0 ? -0.1 : 0.1;
    setZoom((z) => Math.max(1, Math.min(3, +(z + delta).toFixed(2))));
  };

  const resetZoom = () => setZoom(1);

  const openInNewTab = (f: File) => {
    const url = getUrlForFile(f);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={styles.FileButtonContainer}>
      <div className={styles.FileSizeProgressContainer} style={{ marginBottom: files.length ? 6 : 0 }}>
        <div className={styles.FileSizeProgressLabel}>
          {t('cartAI.totalSize', { defaultValue: 'Total:' })}{' '}<strong>{formatMB(totalBytes)}</strong> / {MAX_TOTAL_MB}MB
        </div>
        <div className={styles.FileSizeProgressBarTotal}>
          <div style={{ width: `${percentage}%`, height: '100%', background: color, transition: 'width 220ms ease' }} />
        </div>
        <div className={styles.FileSizeProgressCounter}>
          {files.length}/{MAX_FILES}
        </div>
      </div>

      {files.length > 0 && (
        <div className={styles.FilesContainer}>
          {files.map((f, i) => {
            const short = truncate(f.name, 25);
            const key = `${f.name}_${f.size}_${f.lastModified}`;
            const url = fileUrls[key] || getUrlForFile(f);
            const isImg = isFileImage(f);

            return (
              <div key={`${f.name}-${f.size}-${f.lastModified}-${i}`} className={styles.File} title={f.name}>
                {isImg
                  ? (
                    <img
                      src={url}
                      className={styles.ImagePreview}
                      onClick={() => {
                        setOpenImagePreview(f);
                        setZoom(1);
                      }}
                      alt={f.name}
                    />
                  )
                  : (
                    <button
                      type="button"
                      className={styles.IconPreviewButton}
                      title={t('cartAI.openFile', { defaultValue: 'Open file' })}
                      onClick={() => openInNewTab(f)}
                    >
                      <span className={styles.IconPreview}>
                        {isPDF(f) ? '📄' : isAudioFile(f) ? '🎵' : isVideoFile(f) ? '🎬' : isTextFile(f) ? '📃' : '📎'}
                      </span>
                    </button>
                  )}
                <span className={styles.FileSizeLabel}>{short} <span style={{ opacity: 0.7, fontSize: 11 }}>({formatMB(f.size)})</span></span>
                <button onClick={() => remove(i)} className={styles.ButtonDeleteFile} title={t('cartAI.deleteFile')}>❌</button>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={!!openImagePreview} onClose={() => setOpenImagePreview(null)}>
        <div
          onWheel={handleWheel}
          onDoubleClick={resetZoom}
          onClick={() => setOpenImagePreview(null)}
          style={{ cursor: zoom === 1 ? 'zoom-in' : 'zoom-out' }}
          className={styles.ImagePreviewModal}
        >
          {openImagePreview && (
            <img
              src={getUrlForFile(openImagePreview)}
              alt={openImagePreview.name}
              style={{ transform: `scale(${zoom})` }}
              draggable={false}
            />
          )}
        </div>
      </Modal>

      <button
        type="button"
        className={`rcb-attach-button-${attachDisabled ? 'disabled' : 'enabled'} ${styles.FileButton}`}
        onClick={onClickButtonAttach}
        title={
          attachDisabled
            ? files.length >= MAX_FILES ? t('cartAI.maxFilesReached') : t('cartAI.maxSizeReached')
            : t('cartAI.attachFiles')
        }
        disabled={attachDisabled}
      >
        <span className={`rcb-attach-icon-${attachDisabled ? 'disabled' : 'enabled'}`}><AttachFileIcon /></span>
      </button>

      <input ref={inputRef} type="file" multiple accept={ACCEPTED_FILE_TYPES} style={{ display: 'none' }} onChange={onChange} />
    </div>
  );
};
